import { useEffect, useState } from "react";
import { api, fmtMoney } from "../lib/api";
import Layout from "../components/Layout";
import { PageHeader } from "../components/Shared";
import { Button } from "../components/ui/button";
import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";
import { DownloadSimple, ChartBar, ChartPie } from "@phosphor-icons/react";
import { toast } from "sonner";

const PIE_COLORS = ["#3B82F6", "#10B981", "#F43F5E", "#F59E0B", "#8B5CF6", "#06B6D4", "#EC4899"];

export default function Reports() {
  const [summary, setSummary] = useState(null);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, t] = await Promise.all([api.get("/dashboard/summary"), api.get("/transactions")]);
        setSummary(s.data); setTxns(t.data);
      } catch (e) { toast.error("Yükleme hatası"); }
      finally { setLoading(false); }
    })();
  }, []);

  const doExport = async (entity) => {
    try {
      const res = await api.get(`/reports/export?entity=${entity}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `${entity}.csv`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("CSV indirildi");
    } catch (e) { toast.error("Hata"); }
  };

  const expenseByCat = {};
  txns.filter((t) => t.type === "expense").forEach((t) => {
    expenseByCat[t.category] = (expenseByCat[t.category] || 0) + t.amount;
  });
  const pieData = Object.entries(expenseByCat).map(([name, value]) => ({ name, value: Math.round(value) }));

  if (loading) return <Layout><div className="h-96 flex items-center justify-center"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div></Layout>;

  const exports = [
    { entity: "transactions", label: "Gelir & Gider" },
    { entity: "bank-accounts", label: "Banka Hesapları" },
    { entity: "contacts", label: "Cari Hesaplar" },
    { entity: "payments", label: "Ödeme & Tahsilat" },
    { entity: "checks", label: "Çekler" },
  ];

  return (
    <Layout>
      <PageHeader title="Raporlar" subtitle="Finansal analiz ve dışa aktarma" />

      <div className="bg-card border border-border rounded-md p-6 mb-6" data-testid="export-panel">
        <div className="flex items-center gap-2 mb-4">
          <DownloadSimple size={20} className="text-primary" />
          <h2 className="font-display text-lg font-semibold">CSV Dışa Aktarma</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          {exports.map((e) => (
            <Button key={e.entity} variant="outline" data-testid={`export-${e.entity}`} onClick={() => doExport(e.entity)} className="gap-2">
              <DownloadSimple size={16} /> {e.label}
            </Button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-card border border-border rounded-md p-6" data-testid="monthly-report-chart">
          <div className="flex items-center gap-2 mb-6">
            <ChartBar size={20} className="text-primary" />
            <h2 className="font-display text-lg font-semibold">Aylık Gelir / Gider</h2>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={summary.monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
              <XAxis dataKey="month" stroke="#A1A1AA" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="#A1A1AA" fontSize={12} tickLine={false} axisLine={false} width={40} />
              <Tooltip contentStyle={{ background: "#121214", border: "1px solid #27272A", borderRadius: 8, fontSize: 13 }} />
              <Legend />
              <Bar dataKey="income" fill="#10B981" name="Gelir" radius={[4, 4, 0, 0]} />
              <Bar dataKey="expense" fill="#F43F5E" name="Gider" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-card border border-border rounded-md p-6" data-testid="category-report-chart">
          <div className="flex items-center gap-2 mb-6">
            <ChartPie size={20} className="text-primary" />
            <h2 className="font-display text-lg font-semibold">Gider Dağılımı (Kategori)</h2>
          </div>
          {pieData.length === 0 ? (
            <p className="text-sm text-muted-foreground py-16 text-center">Gider verisi yok</p>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} innerRadius={55} paddingAngle={2}>
                  {pieData.map((e, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#121214", border: "1px solid #27272A", borderRadius: 8, fontSize: 13 }} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </Layout>
  );
}
